import React from 'react'
import { AnimatePresence, motion } from 'motion/react'

/**
 * Shown once, ever, immediately before the first browser location prompt.
 *
 * The browser's own dialog says nothing about why an app wants your location,
 * and a cold "Deny" is close to permanent — Chrome remembers it per origin and
 * we can't ask again. Explaining first is what buys the permission.
 *
 * Declining is durable rather than a deferral, which is why the button says
 * "No thanks" and not "Not now": re-asking on every composer session is
 * precisely the annoyance this whole design avoids. Account settings is the
 * way back, and the modal says so.
 *
 * It shows even when the browser has already granted the permission — say, from
 * the location picker. The plan called for skipping it there, but that would
 * mean starting to store someone's coordinates without ever telling them; the
 * prompt is only half of what this explains. The copy says nothing about the
 * browser asking, so it reads correctly either way.
 */

interface LocationPrimerModalProps {
    isOpen: boolean
    onAccept: () => void
    onDecline: () => void
}

const LocationPrimerModal: React.FC<LocationPrimerModalProps> = ({
    isOpen,
    onAccept,
    onDecline,
}) => (
    <AnimatePresence>
        {isOpen && (
            <motion.div
                className="fixed inset-0 bg-white/75 z-50 flex items-center justify-center p-4"
                style={{ backdropFilter: 'blur(1px)' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="location-primer-title"
            >
                <motion.div
                    className="bg-white rounded-lg p-6 w-full max-w-md shadow-2xl border border-gray-200"
                    initial={{ opacity: 0, scale: 0.95, y: -8 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -4 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                >
                    <h3
                        id="location-primer-title"
                        className="text-lg font-semibold text-gray-900 mb-3"
                    >
                        Tagging places
                    </h3>
                    <div className="text-sm text-gray-600 space-y-3 mb-6">
                        <p>
                            When you mention places, Honeyguide tags them automatically. It
                            will do a much better job of picking the right place if you
                            grant it access to occasionally read your device's location.
                        </p>
                        <p className="text-gray-500">
                            You can turn this off whenever in Account Settings.
                        </p>
                    </div>
                    <div className="flex justify-end gap-3">
                        <motion.button
                            type="button"
                            onClick={onDecline}
                            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        >
                            No thanks
                        </motion.button>
                        <motion.button
                            type="button"
                            onClick={onAccept}
                            className="px-4 py-2 text-sm rounded-md bg-action hover:bg-action-dark text-plum"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        >
                            Use my location
                        </motion.button>
                    </div>
                </motion.div>
            </motion.div>
        )}
    </AnimatePresence>
)

export default LocationPrimerModal
